import { Component, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Location } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { EMPTY, from, firstValueFrom, forkJoin, Observable, of } from 'rxjs';
import { catchError, filter, switchMap, take, timeout } from 'rxjs/operators';
import { AuthService } from '@app/core/services/auth.service';
import { HrmPermissions, MkoPermissions, UserInfoService } from '@app/core/services/user-info.service';
import { HttpOperationErrorResponse } from '@app/core/services/auth-interceptor';
import { AuthRoutes } from './auth-routes';
import { LogService } from '@app/core/services/log.service';
import { Environment } from '@env/environment';
import { SecurityService } from '@app/core/services/security.service';
import { ApplicationFeature } from '@app/core/models/application-feature-enums';
import { LandingRoutes } from '../landing/landing-routes';
import { LoadingIndicatorService } from '@app/core/services/loading-indicator.service';

@Component({
    template: '<app-kpa-spinner></app-kpa-spinner>',
    styleUrls: ['./auth.component.scss'],
    standalone: false
})
export class AuthComponent implements OnInit, OnDestroy {
	private static readonly crossApplicationBootstrapTimeoutMs = 10000;

	hasMko: boolean;
	hasHrDrive: boolean;

	constructor(
		private readonly environment: Environment,
		private readonly authService: AuthService,
		private readonly userInfoService: UserInfoService,
		private readonly router: Router,
		private readonly route: ActivatedRoute,
		private readonly location: Location,
		private readonly log: LogService,
		private readonly securityService: SecurityService,
		private readonly loadingIndicatorService: LoadingIndicatorService,
		private readonly renderer: Renderer2
	) {}

	async ngOnInit(): Promise<void> {
		document.body.classList.add('hide-embedded-messaging');
		const routerOutlet = document.querySelector('body');
		if (routerOutlet) {
			this.renderer.setStyle(routerOutlet, 'padding', '25px');
		}

		this.loadingIndicatorService.startLoading('AuthComponent');
		const routePath = this.route.snapshot.url[0].path;

		this.log.logInformation('ngOnInit', { route: routePath });

		const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
		const error = hashParams.get('error');

		if (error === 'unauthorized') {
			this.router.navigate([AuthRoutes.Blocked]);
			return;
		} else if (error === 'login_required') {
			this.router.navigate([AuthRoutes.Logout]);
			return;
		}

		await this.loadUserPermissions();

		switch (routePath) {
			case AuthRoutes.AuthenticateCallback:
				this.completeAuthenticationAndRedirect();
				break;
			case AuthRoutes.Logout:
				this.authService.logoutAsync(this.hasMko, this.hasHrDrive, false);
				break;
			case AuthRoutes.LogoutCallback:
				// Notify frame of ongoing logout
				const baseAddress = /https:\/\/[^\/]+/.exec(this.environment.services['sso'])[0];
				if (parent) {
					parent.postMessage('kpa.cc.identity.client.externalsignout.loading', baseAddress);
				}
				break;
			case AuthRoutes.ImpersonateCallback:
				this.completeAuthenticationAndRedirect(true);
				break;
			case AuthRoutes.StartImpersonation:
				const userId = this.route.snapshot.paramMap.get('userId');
				this.authService.startImpersonationAsync(userId, AuthRoutes.Landing);
				break;
			case AuthRoutes.EndImpersonation:
				this.authService.endImpersonationAsync(this.hasMko, this.hasHrDrive);
				break;
			case AuthRoutes.EndImpersonationCommit:
				this.authService.commitEndImpersonation(AuthRoutes.Landing);
				break;
			case AuthRoutes.EndImpersonationHRM:
				this.authService.redirectToServerClearHRM(`${window.location.origin}/${AuthRoutes.EndImpersonationCommit}`);
				break;
			case AuthRoutes.ClearSessionHRM:
				this.authService.redirectToServerClearHRM(`${window.location.origin}/${AuthRoutes.LogoutCommit}`);
				break;
			case AuthRoutes.LogoutCommit:
				this.authService.commitLogoutAsync(false);
				break;
			case AuthRoutes.ClearSessionExternalHRM:
				this.authService.redirectToServerClearHRM(`${window.location.origin}/${AuthRoutes.LogoutExternalCommit}`);
				break;
			case AuthRoutes.LogoutExternalCommit:
				this.authService.commitLogoutAsync(true);
				break;
			case AuthRoutes.LoginBootstrapStart:
				this.startCrossApplicationBootstrap(this.route.snapshot.queryParamMap.get('returnUrl'));
				break;
			case AuthRoutes.LoginBootstrapHrm:
				this.continueHrDriveBootstrap();
				break;
			case AuthRoutes.LoginBootstrapCommit:
				this.completeCrossApplicationBootstrap();
				break;
			default:
				this.router.navigate([AuthRoutes.Landing]);
				break;
		}
	}

	/**
	 * Awaits completing authentication and fetching user info before redirecting the user.
	 * @param allowImpersonate True if impersonation is allowed; otherwise false.
	 */
	private completeAuthenticationAndRedirect(allowImpersonate: boolean = false): void {
		from(this.authService.completeAuthenticationAsync(allowImpersonate))
			.pipe(
				switchMap((returnUrl) =>
					this.authService.user.pipe(
						take(1),
						switchMap((user) => {
							if (this.userInfoService.attemptUserProvision(user)) {
								return this.userInfoService.isSsoLocationSelectionRequired(user).pipe(
									take(1),
									switchMap((shouldPromptForLocation) => {
										if (shouldPromptForLocation) {
											this.router.navigate([AuthRoutes.SsoLocationSelection], {
												queryParams: { returnUrl: returnUrl || `/${LandingRoutes.RedirectRoute}` },
											});
											return EMPTY;
										}

										return this.userInfoService.userInfo.pipe(
											filter((info) => !!info),
											take(1),
											switchMap(() =>
												this.securityService.logUserLogin(this.userInfoService.getUserInfoFromStorage().userId).pipe(take(1))
											),
											switchMap(() => of(returnUrl))
										);
									})
								);
							}

							return this.userInfoService.userInfo.pipe(
								filter((info) => !!info),
								take(1),
								switchMap(() => this.securityService.logUserLogin(this.userInfoService.getUserInfoFromStorage().userId).pipe(take(1))),
								switchMap(() => of(returnUrl))
							);
						})
					)
				)
			)
			.subscribe({
				next: (returnUrl) => {
					const safeReturnUrl = this.normalizeReturnUrl(returnUrl);

					if (allowImpersonate) {
						const bootstrapStartUrl = this.buildAbsolutePlatformAuthUrl(AuthRoutes.LoginBootstrapStart, {
							returnUrl: safeReturnUrl,
						});
						if (this.hasMko && this.hasHrDrive) {
							// Clear external sessions first, then rebuild them through the
							// full browser bootstrap flow so impersonated cookies are seeded cleanly.
							const url = this.authService.buildImpersonationRedirectUri(bootstrapStartUrl);
							window.location.assign(url);
						} else if (this.hasMko && !this.hasHrDrive) {
							this.authService.redirectToServerClearMKO(bootstrapStartUrl);
						} else if (this.hasHrDrive && !this.hasMko) {
							this.authService.redirectToServerClearHRM(bootstrapStartUrl);
						} else {
							this.startCrossApplicationBootstrap(safeReturnUrl);
						}
						return;
					}

					this.startCrossApplicationBootstrap(safeReturnUrl);
				},
				error: async (error: Error) => {
					if (error instanceof HttpOperationErrorResponse) {
						// Compute backUrl without toPromise()
						let backUrl = '/landing';
						if (allowImpersonate) {
							const user = await firstValueFrom(this.authService.user.pipe(take(1)));
							backUrl = `/${AuthRoutes.StartImpersonation}/${user?.profile?.sub ?? ''}`;
						}

						await this.authService.logoutAsync(this.hasMko, this.hasHrDrive, true);
						this.location.replaceState(backUrl);
						setTimeout(() => this.router.navigate([`/error/${error.operationId}`]));
					} else {
						this.log.logError(`${error.name}: ${error.message}`, { stack: error.stack });
					}
				},
			});
	}

	/**
	 * Seeds MKO/HRDrive in the browser immediately after Platform login so the
	 * first navigation into those apps can reuse the already-issued Platform token.
	 *
	 * Each downstream app gets a short-lived handoff ticket, redeems the cached
	 * token server-side, writes its own auth cookie, and then returns the browser
	 * to Platform for the next hop.
	 */
	private async startCrossApplicationBootstrap(returnUrl: string | null | undefined): Promise<void> {
		const safeReturnUrl = this.normalizeReturnUrl(returnUrl);

		try {
			await this.loadUserPermissions();

			if (!this.hasMko && !this.hasHrDrive) {
				this.router.navigateByUrl(safeReturnUrl);
				return;
			}

			forkJoin({
				// Ask Security for opaque handoff tickets instead of sending the raw bearer
				// token through the browser URL. Each external app redeems its own ticket.
				mko: this.hasMko ? this.createBootstrapTicket('MKO', () => this.securityService.createMkoTokenHandoff()) : of(null),
				hrm: this.hasHrDrive ? this.createBootstrapTicket('HRDrive', () => this.securityService.createHrmTokenHandoff()) : of(null),
			}).subscribe({
				next: ({ mko, hrm }) => {
					if (this.hasMko && mko?.ticket) {
						const nextReturnUrl =
							this.hasHrDrive && hrm?.ticket
								? this.buildAbsolutePlatformAuthUrl(AuthRoutes.LoginBootstrapHrm, {
										ticket: hrm.ticket,
										returnUrl: safeReturnUrl,
								  })
								: this.buildAbsolutePlatformAuthUrl(AuthRoutes.LoginBootstrapCommit, { returnUrl: safeReturnUrl });

						window.location.assign(this.buildMkoBootstrapUrl(mko.ticket, nextReturnUrl));
						return;
					}

					if (this.hasHrDrive && hrm?.ticket) {
						const nextReturnUrl = this.buildAbsolutePlatformAuthUrl(AuthRoutes.LoginBootstrapCommit, { returnUrl: safeReturnUrl });
						window.location.assign(this.buildHrmBootstrapUrl(hrm.ticket, nextReturnUrl));
						return;
					}

					this.router.navigateByUrl(safeReturnUrl);
				},
				error: (error) => {
					this.log.logWarning('Cross-application login bootstrap was unavailable. Continuing to Platform without external session seeding.', {
						error,
					});
					this.router.navigateByUrl(safeReturnUrl);
				},
			});
		} catch (error) {
			this.log.logWarning('Unable to evaluate cross-application bootstrap permissions. Continuing to Platform.', { error });
			this.router.navigateByUrl(safeReturnUrl);
		}
	}

	private continueHrDriveBootstrap(): void {
		const ticket = this.route.snapshot.queryParamMap.get('ticket');
		const returnUrl = this.normalizeReturnUrl(this.route.snapshot.queryParamMap.get('returnUrl'));

		if (!ticket) {
			this.router.navigateByUrl(returnUrl);
			return;
		}

		// HRDrive's ticket is created up front while Platform still has the user's
		// bearer token in hand. This leg simply spends that pre-issued ticket.
		const commitUrl = this.buildAbsolutePlatformAuthUrl(AuthRoutes.LoginBootstrapCommit, { returnUrl });
		window.location.assign(this.buildHrmBootstrapUrl(ticket, commitUrl));
	}

	/**
	 * Final leg of the bootstrap chain. At this point each external application
	 * has had a chance to seed its browser session, so we can safely continue
	 * to the originally requested Platform route.
	 */
	private completeCrossApplicationBootstrap(): void {
		const returnUrl = this.normalizeReturnUrl(this.route.snapshot.queryParamMap.get('returnUrl'));
		this.router.navigateByUrl(returnUrl);
	}

	/**
	 * Only allow local Platform return paths here. External bootstrap pages get
	 * full absolute URLs separately, but the Angular router should only receive
	 * in-app destinations.
	 */
	private normalizeReturnUrl(returnUrl: string | null | undefined): string {
		const fallbackUrl = `/${LandingRoutes.RedirectRoute}`;
		return returnUrl && returnUrl.startsWith('/') ? returnUrl : fallbackUrl;
	}

	private buildAbsolutePlatformAuthUrl(routePath: string, queryParams: { [key: string]: string }): string {
		const authRoute = this.router.createUrlTree([`/${routePath}`], { queryParams }).toString();
		return `${window.location.origin}${authRoute}`;
	}

	private buildMkoBootstrapUrl(ticket: string, returnUrl: string): string {
		const baseUrl = this.environment.baseDomain['myKpaOnline'].replace(/\/+$/, '');
		return `${baseUrl}/PlatformBootstrap.aspx?ticket=${encodeURIComponent(ticket)}&returnUrl=${encodeURIComponent(returnUrl)}`;
	}

	private buildHrmBootstrapUrl(ticket: string, returnUrl: string): string {
		const baseUrl = this.environment.baseDomain['hrDrive'].replace(/\/+$/, '');
		return `${baseUrl}/Account/PlatformBootstrap?ticket=${encodeURIComponent(ticket)}&returnUrl=${encodeURIComponent(returnUrl)}`;
	}

	private createBootstrapTicket(
		applicationName: string,
		factory: () => Observable<{ ticket: string }>
	) {
		return factory().pipe(
			take(1),
			timeout(AuthComponent.crossApplicationBootstrapTimeoutMs),
			catchError((error) => {
				// One downstream bootstrap failing should not block Platform login or
				// prevent the other downstream app from seeding its session.
				this.log.logWarning(`${applicationName} bootstrap ticket creation failed. Continuing Platform login without that pre-seeded session.`, {
					error,
				});
				return of(null);
			})
		);
	}

	/**
	 * Load user permissions asynchronously after component initialization
	 */
	private async loadUserPermissions(): Promise<void> {
		try {
			const [hasMko, hasHrDrive] = await firstValueFrom(
				forkJoin([
					this.userInfoService.hasPermission(ApplicationFeature.MkoApplication, MkoPermissions.ApplicationMembership).pipe(take(1)),
					this.userInfoService.hasPermission(ApplicationFeature.HrDrive, HrmPermissions.ApplicationMembership).pipe(take(1)),
				])
			);

			this.hasMko = hasMko;
			this.hasHrDrive = hasHrDrive;
			this.log.logInformation('User permissions loaded', { hasMko: hasMko.toString(), hasHrDrive: hasHrDrive.toString() });
		} catch (error) {
			this.log.logError('Failed to load user permissions', { error });
			// Set default values on error
			this.hasMko = false;
			this.hasHrDrive = false;
		}
	}

	ngOnDestroy(): void {
		document.body.classList.remove('hide-embedded-messaging');
		this.renderer.removeStyle(document.body, 'padding');
		this.loadingIndicatorService.stopLoading('AuthComponent');
	}
}
